import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { formatGs, LOCATION_NAMES } from "@/lib/format";
import { Modal, Btn, Input } from "@/components/ui/kit";
import { DatePicker } from "@/components/DatePicker";
import { ProductSearchBar } from "@/components/purchases/ProductSearchBar";
import { toast } from "sonner";
import { Trash2, Minus, Plus } from "lucide-react";

const today = () => new Date().toLocaleDateString("en-CA");
const cell = "h-9 px-2 rounded-md bg-secondary border border-border text-sm font-data focus:border-primary focus:outline-none";

export const PurchaseInvoiceModal = ({ open, onClose, onSaved, products, location, suppliers, initial }) => {
  const [head, setHead] = useState({ supplier: "", invoice_number: "", invoice_date: today(), invoice_type: "con_factura" });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setHead({ supplier: initial?.supplier || "", invoice_number: "", invoice_date: today(), invoice_type: "con_factura" });
    setItems((initial?.items || []).map((r) => ({ ...r, barcode: products.find((p) => p.id === r.product_id)?.barcode || "" })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const addProduct = (p) => {
    const i = items.findIndex((r) => r.product_id === p.id);
    if (i >= 0) { setItems(items.map((r, idx) => idx === i ? { ...r, quantity: Number(r.quantity) + 1 } : r)); toast.success(`${p.name}: +1`); return; }
    setItems([...items, { product_id: p.id, name: p.name, category: p.category, barcode: p.barcode || "", quantity: 1, cost: p.cost || 0, price: p.price || 0 }]);
    if (!head.supplier && p.supplier) setHead((h) => ({ ...h, supplier: p.supplier }));
  };
  const addNew = (text) => {
    const isCode = /^\d{4,}$/.test(text);
    setItems([...items, { product_id: "", name: isCode ? "" : text, category: "General", barcode: isCode ? text : "", quantity: 1, cost: 0, price: 0 }]);
    if (isCode) setTimeout(() => document.querySelector(`[data-testid=purchase-name-${items.length}]`)?.focus(), 50);
  };
  const setRow = (i, f, v) => setItems(items.map((r, idx) => (idx === i ? { ...r, [f]: v } : r)));
  const rmRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  const total = items.reduce((s, r) => s + (Number(r.cost) || 0) * (Number(r.quantity) || 0), 0);
  const units = items.reduce((s, r) => s + (Number(r.quantity) || 0), 0);

  const save = async () => {
    if (items.some((r) => !r.name.trim())) { toast.error("Escribí el nombre de los productos nuevos"); return; }
    const valid = items.filter((r) => Number(r.quantity) > 0);
    if (!valid.length) { toast.error("Agregá al menos un producto"); return; }
    setSaving(true);
    try {
      await api.post("/purchases", { location, ...head, supplier: head.supplier.trim(),
        items: valid.map((r) => ({ product_id: r.product_id || null, name: r.name.trim(), category: r.category, quantity: Number(r.quantity), cost: Number(r.cost) || 0, price: r.price ? Number(r.price) : null, barcode: r.product_id ? "" : r.barcode })) });
      toast.success("Compra registrada y stock actualizado");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Registrar compra de mercadería" xl top>
      <div className="space-y-4" data-testid="purchase-invoice">
        <InvoiceHeader head={head} setHead={setHead} suppliers={suppliers} location={location} />
        <ProductSearchBar products={products} location={location} onPick={addProduct} onNew={addNew} inputRef={searchRef} />
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="purchase-lines">
            <thead className="bg-secondary/60 text-muted-foreground text-xs uppercase">
              <tr><th className="p-2 w-8">#</th><th className="p-2 text-left">Código</th><th className="p-2 text-left">Producto</th><th className="p-2 text-right">Stock</th><th className="p-2 w-32">Cant.</th><th className="p-2 w-32">Costo u.</th><th className="p-2 w-28">P. venta</th><th className="p-2 text-right">Subtotal</th><th className="w-8"></th></tr>
            </thead>
            <tbody>
              {items.map((r, i) => {
                const p = byId[r.product_id];
                return (
                  <tr key={i} className="border-t border-border" data-testid={`purchase-row-${i}`}>
                    <td className="p-2 text-center text-muted-foreground font-data">{i + 1}</td>
                    <td className="p-2 font-data text-xs text-muted-foreground">{r.barcode || "—"}</td>
                    <td className="p-2">
                      {p ? <span className="font-medium">{r.name}</span>
                        : <div className="flex items-center gap-2"><input value={r.name} placeholder="Nombre del producto" onChange={(e) => setRow(i, "name", e.target.value)} data-testid={`purchase-name-${i}`} className={`${cell} flex-1 font-sans`} /><span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-bold">NUEVO</span></div>}
                    </td>
                    <td className="p-2 text-right font-data text-muted-foreground">{p ? (p[`stock_${location}`] || 0) : "—"}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setRow(i, "quantity", Math.max(1, Number(r.quantity) - 1))} className="w-7 h-9 rounded-md hover:bg-accent flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <input type="number" min="1" value={r.quantity} onChange={(e) => setRow(i, "quantity", e.target.value)} data-testid={`purchase-qty-${i}`} className={`${cell} w-14 text-center`} />
                        <button type="button" onClick={() => setRow(i, "quantity", Number(r.quantity) + 1)} className="w-7 h-9 rounded-md hover:bg-accent flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                    </td>
                    <td className="p-2"><input type="number" min="0" value={r.cost} onChange={(e) => setRow(i, "cost", e.target.value)} data-testid={`purchase-cost-${i}`} className={`${cell} w-full text-right`} /></td>
                    <td className="p-2"><input type="number" min="0" value={r.price} onChange={(e) => setRow(i, "price", e.target.value)} data-testid={`purchase-price-${i}`} className={`${cell} w-full text-right`} /></td>
                    <td className="p-2 text-right font-data font-semibold">{formatGs((Number(r.cost) || 0) * (Number(r.quantity) || 0))}</td>
                    <td className="p-2"><button type="button" onClick={() => rmRow(i)} data-testid={`purchase-remove-${i}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground" data-testid="purchase-empty">Escaneá o buscá productos arriba para cargarlos a la factura</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 pt-3 border-t border-border">
          <div className="text-sm text-muted-foreground" data-testid="purchase-summary">{items.length} ítems · {units} unidades</div>
          <div className="flex items-center gap-4">
            <div className="text-right"><div className="text-xs uppercase text-muted-foreground">Total factura</div><div className="font-data text-2xl font-bold text-primary" data-testid="purchase-total">{formatGs(total)}</div></div>
            <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
            <Btn onClick={save} disabled={saving} data-testid="save-purchase">{saving ? "Registrando…" : "Registrar compra"}</Btn>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Stock en {LOCATION_NAMES[location]}. Si cambiás el costo o precio, se actualiza en el producto.</p>
      </div>
    </Modal>
  );
};

const InvoiceHeader = ({ head, setHead, suppliers }) => (
  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 p-4 rounded-xl bg-secondary/40 border border-border">
    <div className="sm:col-span-4"><label className="text-xs uppercase text-muted-foreground mb-1 block">Proveedor</label>
      <Input list="supplier-list" value={head.supplier} onChange={(e) => setHead({ ...head, supplier: e.target.value })} data-testid="purchase-supplier" placeholder="Nombre del proveedor" />
      <datalist id="supplier-list">{suppliers.map((s) => <option key={s} value={s} />)}</datalist>
    </div>
    <div className="sm:col-span-3"><label className="text-xs uppercase text-muted-foreground mb-1 block">N° de factura</label>
      <Input value={head.invoice_number} onChange={(e) => setHead({ ...head, invoice_number: e.target.value })} data-testid="purchase-invoice-number" placeholder="001-001-0000123" className="font-data" />
    </div>
    <div className="sm:col-span-2"><label className="text-xs uppercase text-muted-foreground mb-1 block">Fecha</label>
      <DatePicker value={head.invoice_date} onChange={(v) => setHead({ ...head, invoice_date: v })} testId="purchase-invoice-date" />
    </div>
    <div className="sm:col-span-3"><label className="text-xs uppercase text-muted-foreground mb-1 block">Comprobante</label>
      <div className="grid grid-cols-2 gap-1">
        {[["con_factura", "Con Factura"], ["sin_factura", "Sin Factura"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setHead({ ...head, invoice_type: v })} data-testid={`purchase-inv-${v}`} className={`h-10 rounded-lg text-xs font-semibold border transition-colors ${head.invoice_type === v ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{l}</button>
        ))}
      </div>
    </div>
  </div>
);
